import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { apiProcess } from 'app/config/axios-interceptor';
import { createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState: any = {
  loading: false,
  errorMessage: null,
  entities: [],
  otherPoint: [],
  entity: {},
  otherComment: [],
  viewing: false,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/evaluates';

type IRequest = {
  userId: string;
  ownerId: string;
  planPeriodId: string;
};

export const getEvaluateConfirmComplete = createAsyncThunk(
  'evaluate_confirm_complete/finish_evaluate',
  async ({ userId, ownerId, planPeriodId }: IRequest) => {
    return apiProcess.get<any[]>(`${apiUrl}/finish-evaluate`, {
      params: {
        userId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getResultConfirmComplete = createAsyncThunk(
  'evaluate_confirm_complete/fetch_result_complete',
  async ({ userId, ownerId, planPeriodId, searchText }: any) => {
    return apiProcess.get<any[]>(`${apiUrl}/evaluate-general`, {
      params: {
        userId,
        ownerId,
        planPeriodId,
        searchText,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const saveDraft = createAsyncThunk(
  'evaluate_confirm_complete/save_draft',
  async (payload: any) => {
    return apiProcess.post<any>(`${apiUrl}/finish-evaluate/confirm`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const confirmComplete = createAsyncThunk(
  'evaluate_confirm_complete/agree',
  async (payload: any) => {
    return apiProcess.post<any>(`${apiUrl}/agree`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const rejectProcess = createAsyncThunk(
  'evaluate_confirm_complete/reject',
  async (payload: any) => {
    return apiProcess.post<any>(`${apiUrl}/refuse`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const getOtherPlusComment = createAsyncThunk(
  'evaluate_confirm_complete/get_other_plus_comment',
  async ({ index, documentId, ownerId, evaluateLevelId, planPeriodId }: any) => {
    return apiProcess.get<any>(`${apiUrl}/other-level-pluspoint`, {
      params: { index, documentId, ownerId, evaluateLevelId, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getOtherMinusComment = createAsyncThunk(
  'evaluate_confirm_complete/get_other_minus_comment',
  async ({ index, documentId, ownerId, evaluateLevelId, planPeriodId }: any) => {
    return apiProcess.get<any>(`${apiUrl}/other-level-minuspoint`, {
      params: { index, documentId, ownerId, evaluateLevelId, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getOtherComment = createAsyncThunk(
  'evaluate_confirm_complete/get_other_comment',
  async ({ documentId, ownerId, evaluateLevelId, planPeriodId }: any) => {
    return apiProcess.get<any>(`${apiUrl}/other-level-generalfeedback`, {
      params: { documentId, ownerId, evaluateLevelId, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getOtherPoints = createAsyncThunk(
  'evaluate_confirm_complete/get_other_points',
  async ({ ownerId, reportScheduleId, planPeriodId }: any) => {
    return apiProcess.get<any>(`${apiUrl}/evaluate-score-of-document-versions`, {
      params: { ownerId, reportScheduleId, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);
// slice

export const EvaluateConfirmCompleteEvaluationSlice = createEntitySlice({
  name: 'evaluateConfirmCompleteEvaluation',
  initialState,
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getResultConfirmComplete), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(getOtherPlusComment), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          otherComment: data,
        };
      })
      .addMatcher(isFulfilled(getOtherComment), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          otherComment: data,
        };
      })
      .addMatcher(isFulfilled(getOtherPoints), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          otherPoint: data,
        };
      })
      .addMatcher(isFulfilled(getOtherMinusComment), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          otherComment: data,
        };
      })
      .addMatcher(isFulfilled(getEvaluateConfirmComplete), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(confirmComplete), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(rejectProcess), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(saveDraft), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(saveDraft, confirmComplete, rejectProcess), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(getOtherPlusComment, getOtherComment, getOtherPoints, getOtherMinusComment), state => {
        state.entity = {};
      })
      .addMatcher(isPending(getResultConfirmComplete, getEvaluateConfirmComplete), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entities = [];
      });
  },
});

export const { reset } = EvaluateConfirmCompleteEvaluationSlice.actions;

// Reducer
export default EvaluateConfirmCompleteEvaluationSlice.reducer;
